import { Button, getChannel, Loader, useTheme } from '@ytl/common-web';
import { useEffect, useMemo, useState } from 'react';
import { ReactComponent as PhoneIconDark } from '../../../../assets/phone.svg';
import { ReactComponent as PhoneIconLight } from '../../../../assets/phone_2.svg';
import { useApp } from '../../../../contexts/appContext';
import { useLanguage } from '../../../../contexts/languageContext';
import {
  getAnalytics,
  InitParams,
  StepperControls,
  VerificationPath,
  VerificationPhase,
} from '../../../../modules/verificationComponent/VerificationComponent';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { verificatorResource } from '../../../resource/verificator.resource';
import { emitProcessPhase, emitWidgetEvent } from '../../../utils/emitData';
import { formatMsisdn, formatPhoneNumberWithSpaces } from '../../../utils/phoneNumberFormatter';
import { SelectableOptionItem, SelectableOptions } from '../../common/SelectableOptions';

type PhoneOption = {
  id: string;
  label: string;
  isDefault?: boolean;
};

type SelectPhoneNumberProps = {
  stepper: StepperControls;
  initParams?: InitParams | null;
  phoneOptions?: PhoneOption[];
  onSelect?: (selectedId: string | null) => void;
  onAddNew?: () => void;
  onContinue?: (selectedId: string | null) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  sessionId?: string;
  setSessionId?: (sessionId: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isFinished: boolean;
  isPhoneNumbersLoading: boolean;
  corrId?: string;
  enableAnalytics?: boolean;
};

export const SelectPhoneNumber = ({
  stepper,
  initParams,
  phoneOptions: phoneOptionsProp,
  onSelect,
  onAddNew,
  onContinue,
  setPhoneNumber,
  sessionId,
  isLoading,
  setIsLoading,
  isFinished,
  isPhoneNumbersLoading,
  corrId,
  enableAnalytics = false,
}: SelectPhoneNumberProps) => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  const { isWidgetMode } = useApp();

  const borgAnalytics = getAnalytics(enableAnalytics);

  const phoneOptions = useMemo(
    () =>
      phoneOptionsProp && phoneOptionsProp.length > 0
        ? phoneOptionsProp
        : initParams?.salesMsisdn
          ? [
              {
                id: 'HasInitParamSalesMsisdnJustBoughtNumberOption',
                label: translation.yeppRegistration.webcomponent.useCurrentPhone,
                isDefault: true,
              },
            ]
          : [],
    [phoneOptionsProp, initParams?.salesMsisdn, translation.yeppRegistration.webcomponent.useCurrentPhone],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Select the first option when phoneOptions change
  useEffect(() => {
    if (phoneOptions.length > 0) {
      setSelectedId(phoneOptions[0]?.id ?? null);
    }
  }, [phoneOptions]);

  useEffect(() => {
    emitProcessPhase('SelectPhoneNumber');
  }, []);
  const [isCustomPhone, setIsCustomPhone] = useState(false);

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: 'Ctn authentication start',
    });
  }, [borgAnalytics, isWebView]);

  // Convert PhoneOption[] to SelectableOptionItem[]
  const selectableOptions: SelectableOptionItem[] = useMemo(() => {
    return phoneOptions.map(option => ({
      id: option.id,
      label: formatPhoneNumberWithSpaces(option.label),
      isDefault: option.isDefault,
      icon: isWebView ? <PhoneIconDark /> : <PhoneIconLight />,
    }));
  }, [phoneOptions, isWebView]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setIsCustomPhone(false);
    stepper.removePath(VerificationPath.CustomPhoneNumber);
    onSelect?.(id);
  };

  const handleAddNew = () => {
    setSelectedId(null);
    setIsCustomPhone(true);
    stepper.addPath(VerificationPath.CustomPhoneNumber);
    onSelect?.(null);
    onAddNew?.();
    stepper.goToNext();
  };

  const handleContinue = async () => {
    const selectedOption = phoneOptions.find(opt => opt.id === selectedId);
    const phoneNumber = formatMsisdn(
      selectedOption?.id === 'HasInitParamSalesMsisdnJustBoughtNumberOption'
        ? initParams?.salesMsisdn || ''
        : selectedOption?.label || '',
    );
    setPhoneNumber(phoneNumber);
    setIsLoading(true);

    try {
      const verifyResponsePhone = await verificatorResource.validateMsisdn(
        {
          sessionId: sessionId,
          localPhoneNumber: phoneNumber.startsWith('+') ? phoneNumber : `+36${phoneNumber}`,
        },
        corrId,
      );

      // Check for HTTP error status codes (4xx and 5xx)
      if (verifyResponsePhone.status >= 400) {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'validateMsisdn',
          action: 'TechnicalError',
          result: 'false',
          reason: `HTTP Error ${verifyResponsePhone.status}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'validateMsisdn',
              details: `HTTP Error ${verifyResponsePhone.status}: ${verifyResponsePhone.statusText}`,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        return;
      }

      // Check for application-level errors in response data
      if (verifyResponsePhone.data.status === 'error') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'validateMsisdn',
          action: 'TechnicalError',
          result: 'false',
          reason: `${verifyResponsePhone.data}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'validateMsisdn',
              details: verifyResponsePhone.data,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        return;
      }
      if (verifyResponsePhone.data.status === 'banned') {
        setIsLoading(false);
        stepper.goToPhase(VerificationPhase.Banned);
        return;
      }
      if (verifyResponsePhone.data.status === 'invalid') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'validateMsisdn',
          action: 'TechnicalError',
          result: 'false',
          reason: `${verifyResponsePhone.data}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'validateMsisdn',
              details: verifyResponsePhone.data,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        stepper.goToPhase(VerificationPhase.Error);
        return;
      }
    } catch (error) {
      console.error('Failed to validate MSISDN:', error);
      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'validateMsisdn',
        action: 'TechnicalError',
        result: 'false',
        reason: `${error}`,
      });
      emitWidgetEvent(
        {
          eventName: WidgetEvent.TechnicalError,
          eventValue: {
            process: 'validateMsisdn',
            details: error,
          },
          isSuccess: false,
        },
        { sessionId: sessionId || '' },
      );
      setIsLoading(false);
      return;
    }

    borgAnalytics.sendRegistrationEvent({
      channel: getChannel(isWebView),
      virtualName: 'Ctn authentication start',
      action: 'SMSVerificationBypass',
      result: 'true',
    });
    emitWidgetEvent(
      {
        eventName: WidgetEvent.SMSVerificationBypass,
        eventValue: {},
        isSuccess: true,
      },
      { sessionId: sessionId || '' },
    );
    onContinue?.(selectedId);
    stepper.goToNext();
  };

  return (
    <div className={`verification-step${isWebView ? ' verification-step--webview' : ''}`}>
      {!isWidgetMode && (
        <p className="verification-step__options-header">{translation.yeppRegistration.webcomponent.voiceSubs}</p>
      )}
      {isPhoneNumbersLoading ? (
        <div className="verification-step__loader">
          <Loader isLoading />
        </div>
      ) : (
        <SelectableOptions
          options={selectableOptions}
          selectedId={selectedId}
          onSelect={handleSelect}
          showAddNew={true}
          addNewLabel={translation.yeppRegistration.webcomponent.enterDifferentPhone}
          isAddNewSelected={isCustomPhone}
          onAddNew={handleAddNew}
          disabled={isLoading || isPhoneNumbersLoading || isFinished}
        />
      )}
      <div className={`verification-step__actions${isWebView ? ' verification-step__actions--webview' : ''}`}>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={isLoading || isPhoneNumbersLoading || isFinished || selectableOptions.length === 0 || !selectedId}
        >
          {translation.yeppRegistration.webcomponent.continue}
        </Button>
      </div>
    </div>
  );
};
